import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { 
  BarChart3, 
  TrendingUp,
  AlertTriangle,
  Filter,
  RefreshCw,
  DollarSign,
  Package,
  Building2,
  Star,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  X,
  CheckCircle2,
  AlertCircle,
  Target,
  Grid3X3,
  LayoutGrid,
  Settings2,
  Sparkles,
  Plus,
  Car,
  Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import {
  Alert,
  AlertDescription,
} from '../ui/alert';
import MarketShareMatrix from './MarketShareMatrix';

// Format large numbers
const formatVolume = (vol) => {
  if (vol >= 1000000000) return `${(vol / 1000000000).toFixed(1)}B`;
  if (vol >= 1000000) return `${(vol / 1000000).toFixed(1)}M`;
  if (vol >= 1000) return `${(vol / 1000).toFixed(0)}K`;
  return vol?.toString() || '0';
};

// Format currency
const formatCurrency = (amount) => {
  if (amount >= 1000000000) return `$${(amount / 1000000000).toFixed(1)}B`;
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
  return `$${amount?.toFixed(0) || 0}`;
};

// Info tooltip component
const InfoTooltip = ({ content }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline-block ml-1" />
    </TooltipTrigger>
    <TooltipContent className="max-w-xs bg-slate-900 text-white">
      <p className="text-xs">{content}</p>
    </TooltipContent>
  </Tooltip>
);

// Colors for competitors
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Simple pie chart component using CSS
const SimplePieChart = ({ data, size = 160 }) => {
  if (!data || data.length === 0) return null;
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
  if (total === 0) return null;
  
  const segments = data.reduce((acc, item, index) => {
    const percent = (item.value / total) * 100;
    const start = index === 0 ? 0 : acc[index - 1].end;
    const end = start + percent;
    return [...acc, { ...item, start, end, percent }];
  }, []);
  
  const gradientParts = segments.map((seg) => `${seg.color} ${seg.start}% ${seg.end}%`).join(', ');
  
  return (
    <div className="flex items-center gap-6">
      <div 
        className="rounded-full flex-shrink-0"
        style={{ width: size, height: size, background: `conic-gradient(${gradientParts})` }}
      />
      <div className="space-y-1.5">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded flex-shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="text-slate-600 truncate max-w-[120px]" title={seg.label}>{seg.label}</span>
            <span className="font-medium text-slate-900">{seg.percent.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Part Type Card Component - Updated to handle unconfigured state
const PartTypeCard = ({ partType, isExpanded, onToggle, onConfigure }) => {
  const myShare = partType.my_company;
  const competitors = partType.competitor_shares || [];
  const isConfigured = partType.is_configured;
  
  const pieData = [];
  competitors.forEach((comp, i) => {
    if (comp.estimated_share_percent > 0) {
      pieData.push({
        label: comp.is_my_company ? `${comp.competitor_name} (You)` : comp.competitor_name,
        value: comp.estimated_volume,
        color: comp.is_my_company ? '#3b82f6' : COLORS[(i + 1) % COLORS.length]
      });
    }
  });
  if (partType.available_percent > 0) {
    pieData.push({ label: 'Available', value: partType.available_volume, color: '#e2e8f0' });
  }
  
  // Show unconfigured state
  if (!isConfigured) {
    return (
      <Card className="overflow-hidden border-amber-200 bg-amber-50/30">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5 text-amber-500" />
                {partType.part_type_name}
              </CardTitle>
              <CardDescription className="mt-1">
                {partType.part_type_code && <Badge variant="outline" className="mr-2">{partType.part_type_code}</Badge>}
                ${partType.price_per_unit_dollars?.toFixed(2)} per unit
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-100">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Not Configured
            </Badge>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="bg-white border border-amber-200 rounded-lg p-4 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-amber-700 font-medium">No market share data</p>
            <p className="text-xs text-amber-600 mt-1">
              Configure competitor assignments for this part type
            </p>
            <Button 
              size="sm" 
              className="mt-3 bg-amber-600 hover:bg-amber-700"
              onClick={onConfigure}
            >
              <Settings2 className="h-4 w-4 mr-2" />
              Configure
            </Button>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-center">
            <div className="bg-white/50 rounded-lg p-3">
              <p className="text-xs text-slate-500 uppercase">Total Market</p>
              <p className="text-lg font-semibold text-slate-900">{formatVolume(partType.total_market_volume)}</p>
              <p className="text-xs text-slate-400">{formatCurrency(partType.total_market_revenue_dollars)}</p>
            </div>
            <div className="bg-amber-100/50 rounded-lg p-3">
              <p className="text-xs text-amber-600 uppercase">Unassigned</p>
              <p className="text-lg font-semibold text-amber-700">100%</p>
              <p className="text-xs text-amber-500">{formatCurrency(partType.total_market_revenue_dollars)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className={`overflow-hidden ${partType.over_100_warning ? 'border-amber-300' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-slate-400" />
              {partType.part_type_name}
              {partType.over_100_warning && (
                <Tooltip>
                  <TooltipTrigger>
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Total assigned share exceeds 100%</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </CardTitle>
            <CardDescription className="mt-1">
              {partType.part_type_code && <Badge variant="outline" className="mr-2">{partType.part_type_code}</Badge>}
              ${partType.price_per_unit_dollars?.toFixed(2)} per unit
            </CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={onToggle} className="text-slate-400 hover:text-slate-600">
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Star className="h-4 w-4 text-blue-600" />
            <span className="font-medium text-blue-900">{myShare?.name || 'Your Company'}</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-blue-600 uppercase tracking-wide">Share</p>
              <p className="text-xl font-bold text-blue-900">{myShare?.share_percent || 0}%</p>
            </div>
            <div>
              <p className="text-xs text-blue-600 uppercase tracking-wide">Volume</p>
              <p className="text-xl font-bold text-blue-900">{formatVolume(myShare?.volume || 0)}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600 uppercase tracking-wide">Revenue</p>
              <p className="text-xl font-bold text-blue-900">{formatCurrency(myShare?.revenue_dollars || 0)}</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase">Total Market</p>
            <p className="text-lg font-semibold text-slate-900">{formatVolume(partType.total_market_volume)}</p>
            <p className="text-xs text-slate-400">{formatCurrency(partType.total_market_revenue_dollars)}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3">
            <p className="text-xs text-amber-600 uppercase">Available</p>
            <p className="text-lg font-semibold text-amber-700">{partType.available_percent}%</p>
            <p className="text-xs text-amber-500">{formatCurrency(partType.available_revenue_dollars)}</p>
          </div>
        </div>
        
        {isExpanded && (
          <div className="space-y-4 pt-4 border-t">
            <div className="flex justify-center">
              <SimplePieChart data={pieData} size={140} />
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-700">Competitor Breakdown</h4>
              {competitors.map((comp, i) => (
                <div key={comp.competitor_id} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded" style={{ backgroundColor: comp.is_my_company ? '#3b82f6' : COLORS[(i + 1) % COLORS.length] }} />
                      <span className={comp.is_my_company ? 'font-medium text-blue-600' : 'text-slate-700'}>
                        {comp.competitor_name}{comp.is_my_company && ' (You)'}
                      </span>
                    </div>
                    <span className="font-medium">{comp.estimated_share_percent}%</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-500 pl-4">
                    <span>{formatVolume(comp.estimated_volume)} units</span>
                    <span>•</span>
                    <span>{formatCurrency(comp.estimated_revenue_dollars)}</span>
                  </div>
                  <Progress value={comp.estimated_share_percent} className="h-1.5" />
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-400 text-center pt-2">
              Based on {partType.rules_count} segment rule{partType.rules_count !== 1 ? 's' : ''}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Coverage Assignment Panel
const CoverageAssignmentPanel = ({ isOpen, onClose, selectedOem, programs, competitors, onAssign }) => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedCompetitor, setSelectedCompetitor] = useState('');
  const [assignmentType, setAssignmentType] = useState('segment_rule');
  const [sharePercent, setSharePercent] = useState('30');
  
  const handleAssign = () => {
    if (!selectedProgram || !selectedCompetitor) return;
    onAssign({
      core_mnemonic: selectedProgram.core_mnemonic,
      competitor_id: selectedCompetitor,
      assignment_type: assignmentType,
      oem: selectedProgram.oem,
      region: selectedProgram.region,
      estimated_share_percent: parseFloat(sharePercent)
    });
    setSelectedProgram(null);
    setSelectedCompetitor('');
  };
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-amber-600" />
            Improve Coverage{selectedOem ? `: ${selectedOem}` : ''}
          </DialogTitle>
          <DialogDescription>
            Assign competitors to programs to improve market share accuracy
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Programs List */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Select Program to Assign</Label>
            <div className="border rounded-lg max-h-48 overflow-y-auto">
              {programs?.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
                  All high-volume programs are assigned!
                </div>
              ) : (
                programs?.map((program) => (
                  <div
                    key={program.core_mnemonic}
                    className={`p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${
                      selectedProgram?.core_mnemonic === program.core_mnemonic ? 'bg-blue-50 border-blue-200' : ''
                    }`}
                    onClick={() => setSelectedProgram(program)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900">{program.production_nameplate || program.program}</p>
                        <p className="text-xs text-slate-500">{program.oem} • {program.region}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-blue-600">{formatVolume(program.annual_volume)}</p>
                        <p className="text-xs text-slate-400">units/year</p>
                      </div>
                    </div>
                    {program.has_segment_rule && (
                      <Badge variant="outline" className="mt-1 text-xs">Has segment rule</Badge>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
          
          {selectedProgram && (
            <>
              {/* Competitor Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Assign to Competitor</Label>
                <Select value={selectedCompetitor} onValueChange={setSelectedCompetitor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select competitor..." />
                  </SelectTrigger>
                  <SelectContent>
                    {competitors?.map((comp) => (
                      <SelectItem key={comp.id} value={comp.id}>
                        <span className="flex items-center gap-1.5">
                          {comp.is_my_company && <Star className="h-3 w-3 text-blue-500" />}
                          {comp.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Assignment Type */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Assignment Type</Label>
                <RadioGroup value={assignmentType} onValueChange={setAssignmentType}>
                  <div className="flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50">
                    <RadioGroupItem value="carline_override" id="carline" className="mt-1" />
                    <div>
                      <Label htmlFor="carline" className="font-medium cursor-pointer">This program only</Label>
                      <p className="text-xs text-slate-500">Creates a specific override for {selectedProgram.production_nameplate}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 border rounded-lg hover:bg-slate-50">
                    <RadioGroupItem value="segment_rule" id="segment" className="mt-1" />
                    <div>
                      <Label htmlFor="segment" className="font-medium cursor-pointer">All similar programs (Segment Rule)</Label>
                      <p className="text-xs text-slate-500">Applies to all {selectedProgram.oem} / {selectedProgram.region || 'All Regions'} programs</p>
                    </div>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Share Percent (for segment rules) */}
              {assignmentType === 'segment_rule' && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Estimated Market Share %</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={sharePercent}
                    onChange={(e) => setSharePercent(e.target.value)}
                    className="w-32"
                  />
                  <p className="text-xs text-slate-500">What % of {selectedProgram.oem} market does this competitor have?</p>
                </div>
              )}
              
              {/* Action Button */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setSelectedProgram(null)}>Cancel</Button>
                <Button onClick={handleAssign} disabled={!selectedCompetitor}>
                  Assign Competitor
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const MarketShareDashboard = () => {
  const queryClient = useQueryClient();
  const [selectedOem, setSelectedOem] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [expandedPartTypes, setExpandedPartTypes] = useState(new Set());
  const [coveragePanelOpen, setCoveragePanelOpen] = useState(false);
  const [coverageFilterOem, setCoverageFilterOem] = useState(null);
  const [showMatrixView, setShowMatrixView] = useState(false);
  const [showTemplatePrompt, setShowTemplatePrompt] = useState(false);

  // Fetch available OEMs
  const { data: oemsData } = useQuery({
    queryKey: ['availableOems'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/available-oems');
      return response.data;
    }
  });

  // Fetch available regions
  const { data: regionsData } = useQuery({
    queryKey: ['availableRegions'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/available-regions');
      return response.data;
    }
  });

  // Fetch market share by part type - using V2 API now
  const { data: partTypeData, isLoading: partTypeLoading, refetch: refetchPartTypes } = useQuery({
    queryKey: ['marketShareByPartTypeV2', selectedOem, selectedRegion, selectedYear],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedOem && selectedOem !== 'all') params.append('oem', selectedOem);
      if (selectedRegion && selectedRegion !== 'all') params.append('region', selectedRegion);
      params.append('year', selectedYear);
      const response = await apiClient.get(`/api/competitive-intelligence/market-share/by-part-type-v2?${params}`);
      return response.data;
    }
  });

  // Fetch summary data
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['marketShareSummary', selectedOem, selectedRegion, selectedYear],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedOem && selectedOem !== 'all') params.append('oem', selectedOem);
      if (selectedRegion && selectedRegion !== 'all') params.append('region', selectedRegion);
      params.append('year', selectedYear);
      const response = await apiClient.get(`/api/competitive-intelligence/market-share/summary-all?${params}`);
      return response.data;
    }
  });

  // Fetch coverage by OEM
  const { data: coverageData } = useQuery({
    queryKey: ['coverageByOem', selectedYear],
    queryFn: async () => {
      const response = await apiClient.get(`/api/competitive-intelligence/coverage/by-oem?year=${selectedYear}`);
      return response.data;
    }
  });

  // Fetch unassigned programs
  const { data: unassignedData } = useQuery({
    queryKey: ['unassignedPrograms', coverageFilterOem, selectedYear],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (coverageFilterOem) params.append('oem', coverageFilterOem);
      params.append('year', selectedYear);
      params.append('limit', '15');
      const response = await apiClient.get(`/api/competitive-intelligence/coverage/unassigned-programs?${params}`);
      return response.data;
    },
    enabled: coveragePanelOpen
  });

  // Fetch competitors for assignment
  const { data: competitorsData } = useQuery({
    queryKey: ['competitorsList'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/competitors?is_active=true');
      return response.data;
    }
  });

  // Quick assign mutation
  const assignMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiClient.post('/api/competitive-intelligence/coverage/quick-assign', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['coverageByOem']);
      queryClient.invalidateQueries(['unassignedPrograms']);
      queryClient.invalidateQueries(['marketShareByPartType']);
      queryClient.invalidateQueries(['marketShareSummary']);
    }
  });

  const oems = oemsData?.oems || [];
  const regions = regionsData?.regions || [];
  const partTypes = partTypeData?.part_types || [];
  const myCompany = partTypeData?.my_company || summaryData?.my_company;
  const coverageOems = coverageData?.oems || [];

  const togglePartType = (id) => {
    setExpandedPartTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const expandAll = () => setExpandedPartTypes(new Set(partTypes.map(pt => pt.part_type_id)));
  const collapseAll = () => setExpandedPartTypes(new Set());

  const openCoveragePanel = (oem = null) => {
    setCoverageFilterOem(oem);
    setCoveragePanelOpen(true);
  };

  const handleAssign = (data) => {
    assignMutation.mutate(data);
  };

  // Calculate totals
  const myCompanyTotals = { totalVolume: 0, totalRevenue: 0, avgSharePercent: 0 };
  if (partTypes.length > 0) {
    let totalShare = 0, shareCount = 0;
    partTypes.forEach(pt => {
      if (pt.my_company) {
        myCompanyTotals.totalVolume += pt.my_company.volume || 0;
        myCompanyTotals.totalRevenue += pt.my_company.revenue_dollars || 0;
        if (pt.my_company.share_percent > 0) {
          totalShare += pt.my_company.share_percent;
          shareCount++;
        }
      }
    });
    myCompanyTotals.avgSharePercent = shareCount > 0 ? totalShare / shareCount : 0;
  }

  return (
    <TooltipProvider>
    <div className="space-y-6">
      {/* Header with Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Market Share Analysis</h2>
          <p className="text-sm text-slate-500 mt-1">
            Revenue and market share by part type
            <InfoTooltip content="Market share is calculated based on segment rules you define. Revenue = Volume × Part Type Price." />
          </p>
        </div>
        
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027, 2028].map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Select value={selectedOem} onValueChange={setSelectedOem}>
            <SelectTrigger className="w-48"><SelectValue placeholder="All OEMs" /></SelectTrigger>
            <SelectContent className="max-h-60">
              <SelectItem value="all">All OEMs</SelectItem>
              {oems.slice(0, 50).map(oem => (<SelectItem key={oem} value={oem}>{oem}</SelectItem>))}
            </SelectContent>
          </Select>
          
          <Select value={selectedRegion} onValueChange={setSelectedRegion}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Regions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Regions</SelectItem>
              {regions.map(region => (<SelectItem key={region} value={region}>{region}</SelectItem>))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={() => refetchPartTypes()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* My Company Summary Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <Building2 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-blue-900">{myCompany?.name || 'Your Company'}</h3>
              <p className="text-sm text-blue-600">Overall Market Position</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">
                Avg Market Share
                <InfoTooltip content="Average of your market share percentages across all part types. Based on segment rules you've defined." />
              </p>
              <p className="text-3xl font-bold text-blue-900">
                {summaryLoading ? '...' : `${summaryData?.my_company?.share_percent?.toFixed(1) || myCompanyTotals.avgSharePercent.toFixed(1)}%`}
              </p>
            </div>
            <div>
              <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">
                Total Volume
                <InfoTooltip content="Estimated total units based on your market share percentage × total IHS market volume." />
              </p>
              <p className="text-3xl font-bold text-blue-900">
                {summaryLoading ? '...' : formatVolume(summaryData?.my_company?.volume || myCompanyTotals.totalVolume)}
              </p>
            </div>
            <div>
              <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">
                Total Revenue
                <InfoTooltip content="Estimated revenue = Volume × Part Type Price (from Part Types configuration)." />
              </p>
              <p className="text-3xl font-bold text-blue-900">
                {summaryLoading ? '...' : formatCurrency(summaryData?.my_company?.revenue_dollars || myCompanyTotals.totalRevenue)}
              </p>
            </div>
            <div>
              <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Part Types</p>
              <p className="text-3xl font-bold text-blue-900">{partTypeLoading ? '...' : partTypes.length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Market Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Total Market Volume
                  <InfoTooltip content="Total vehicle units from IHS data across all programs matching your filters." />
                </p>
                <p className="text-2xl font-semibold text-slate-900 mt-1">
                  {summaryLoading ? '...' : formatVolume(summaryData?.total_market?.volume || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-1">{summaryData?.total_market?.carlines?.toLocaleString() || 0} programs</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-50"><BarChart3 className="h-5 w-5 text-slate-600" /></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Total Market Revenue
                  <InfoTooltip content="Total market volume × average price per unit, summed across all part types." />
                </p>
                <p className="text-2xl font-semibold text-green-600 mt-1">
                  {summaryLoading ? '...' : formatCurrency(summaryData?.total_market?.revenue_dollars || 0)}
                </p>
                <p className="text-xs text-slate-500 mt-1">Across all part types</p>
              </div>
              <div className="p-3 rounded-lg bg-green-50"><DollarSign className="h-5 w-5 text-green-600" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => openCoveragePanel()}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">
                  Available Market
                  <InfoTooltip content="Market share not assigned to any competitor. Click to improve coverage by assigning competitors." />
                </p>
                <p className="text-2xl font-semibold text-amber-600 mt-1">
                  {summaryLoading ? '...' : `${summaryData?.available?.percent?.toFixed(1) || 0}%`}
                </p>
                <p className="text-xs text-amber-600 mt-1 font-medium">Click to improve coverage →</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* OEM Opportunities - Redesigned */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-500" />
                Assign Competitors by OEM
              </CardTitle>
              <CardDescription>Your top OEMs by volume - assign competitors to improve market share accuracy</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => openCoveragePanel()}>
              View All OEMs
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {coverageOems.slice(0, 8).map((oem) => {
              const isAssigned = oem.total_assigned_percent > 0;
              const isComplete = oem.total_assigned_percent >= 90;
              
              return (
                <div 
                  key={oem.oem} 
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                    isComplete 
                      ? 'bg-green-50/50 border-green-200' 
                      : isAssigned 
                        ? 'bg-blue-50/30 border-blue-200' 
                        : 'bg-slate-50/50 border-slate-200 hover:border-blue-300 hover:bg-blue-50/30'
                  }`}
                >
                  {/* Status Icon */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    isComplete ? 'bg-green-100' : isAssigned ? 'bg-blue-100' : 'bg-slate-100'
                  }`}>
                    {isComplete ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : isAssigned ? (
                      <TrendingUp className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Car className="h-4 w-4 text-slate-400" />
                    )}
                  </div>
                  
                  {/* OEM Name & Volume */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 truncate" title={oem.oem}>
                        {oem.oem}
                      </span>
                      {isComplete && (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                          Complete
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                      <span>{formatVolume(oem.total_volume)} units/year</span>
                      {isAssigned && (
                        <>
                          <span>•</span>
                          <span className="text-blue-600">{oem.total_assigned_percent}% assigned</span>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Quick Assign or Status */}
                  <div className="flex-shrink-0">
                    {isComplete ? (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-green-600 hover:text-green-700 hover:bg-green-100"
                        onClick={() => openCoveragePanel(oem.oem)}
                      >
                        View
                      </Button>
                    ) : (
                      <Button 
                        variant={isAssigned ? "outline" : "default"}
                        size="sm" 
                        className={isAssigned ? "" : "bg-blue-600 hover:bg-blue-700"}
                        onClick={() => openCoveragePanel(oem.oem)}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        {isAssigned ? 'Add More' : 'Assign'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Summary Footer */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t text-sm">
            <div className="flex items-center gap-4 text-slate-500">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                {coverageOems.filter(o => o.total_assigned_percent >= 90).length} complete
              </span>
              <span className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-blue-500" />
                {coverageOems.filter(o => o.total_assigned_percent > 0 && o.total_assigned_percent < 90).length} in progress
              </span>
              <span className="flex items-center gap-1.5">
                <Car className="h-3.5 w-3.5 text-slate-400" />
                {coverageOems.filter(o => o.total_assigned_percent === 0).length} to assign
              </span>
            </div>
            {coverageOems.length > 8 && (
              <span className="text-slate-400">+{coverageOems.length - 8} more OEMs</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Configuration Status Banner - Show if there are unconfigured part types and templates available */}
      {partTypeData?.configuration_status?.unconfigured > 0 && partTypeData?.configuration_status?.has_templates && (
        <Alert className="bg-purple-50 border-purple-200">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <AlertDescription className="flex items-center justify-between">
            <span className="text-purple-900">
              <strong>{partTypeData.configuration_status.unconfigured} part type(s)</strong> need configuration. 
              You have <strong>{partTypeData.configuration_status.templates_count} template rule(s)</strong> that can be applied.
            </span>
            <Button 
              size="sm" 
              variant="outline"
              className="ml-4 border-purple-300 text-purple-700 hover:bg-purple-100"
              onClick={() => setShowMatrixView(true)}
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              Open Matrix View
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Part Types Grid / Matrix View Toggle */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-slate-900">Market Share by Part Type</h3>
            {partTypeData?.configuration_status && (
              <Badge 
                variant={partTypeData.configuration_status.configured === partTypeData.configuration_status.total ? "default" : "secondary"}
                className="text-xs"
              >
                {partTypeData.configuration_status.configured} / {partTypeData.configuration_status.total} Configured
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button 
              variant={showMatrixView ? "default" : "outline"} 
              size="sm" 
              onClick={() => setShowMatrixView(!showMatrixView)}
            >
              <Grid3X3 className="h-4 w-4 mr-2" />
              {showMatrixView ? 'Exit Matrix' : 'Bulk Edit'}
            </Button>
            {!showMatrixView && (
              <>
                <Button variant="outline" size="sm" onClick={expandAll}>Expand All</Button>
                <Button variant="outline" size="sm" onClick={collapseAll}>Collapse All</Button>
              </>
            )}
          </div>
        </div>
        
        {showMatrixView ? (
          <Card>
            <CardContent className="p-6">
              <MarketShareMatrix 
                onClose={() => setShowMatrixView(false)}
                onSaveSuccess={(data) => {
                  refetchPartTypes();
                }}
              />
            </CardContent>
          </Card>
        ) : partTypeLoading ? (
          <div className="text-center py-12 text-slate-500">Loading part types...</div>
        ) : partTypes.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No part types defined yet.</p>
              <p className="text-sm text-slate-400 mt-1">Add part types to see market share analysis.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {partTypes.map(pt => (
              <PartTypeCard 
                key={pt.part_type_id}
                partType={pt}
                isExpanded={expandedPartTypes.has(pt.part_type_id)}
                onToggle={() => togglePartType(pt.part_type_id)}
                onConfigure={() => setShowMatrixView(true)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Coverage Assignment Panel */}
      <CoverageAssignmentPanel
        isOpen={coveragePanelOpen}
        onClose={() => setCoveragePanelOpen(false)}
        selectedOem={coverageFilterOem}
        programs={unassignedData?.programs || []}
        competitors={competitorsData || []}
        onAssign={handleAssign}
      />
    </div>
    </TooltipProvider>
  );
};

export default MarketShareDashboard;
